/**
 * De scène als document. Alles wat je in het instellingenmenu kunt draaien staat
 * hier — de 3D-componenten lezen uitsluitend hieruit en kennen geen eigen
 * constanten meer.
 */

export interface LichtConfig {
  omgeving: number
  ambient: number
  zonSterkte: number
  zonKleur: string
  armatuurSterkte: number
  armatuurKleur: string
  accentSterkte: number
  accentKleur: string
  mistNabij: number
  mistVer: number
  mistKleur: string
  schaduwen: boolean
  /**
   * Vullicht van de armaturen: een schaduwloos licht recht van boven. Het
   * hoofdlicht (`zon`) valt sinds de daklichten alleen nog dóór die daklichten
   * naar binnen — het dak houdt de rest tegen — dus zonder deze vulling zou
   * alles buiten de zonvlekken zwart zijn.
   */
  vulSterkte: number
  /** Lichtstraten in het dak, met de zon die erdoor valt. */
  daklichten: boolean
  /** Zichtbare lichtschachten onder de daklichten, in het stof. */
  lichtschachten: boolean
  schachtSterkte: number
  /** Lichtband (doorschijnende panelen) bovenin de gevels. */
  lichtband: boolean
  /** Hoe fel daglicht buiten is ten opzichte van binnen. Het oog verwacht een factor 10+. */
  daglicht: number
  /** Zichtbare lichtkegels onder de armaturen. */
  bundels: boolean
  bundelSterkte: number
  /** Lichtvlekken op de vloer onder elk armatuur. */
  lichtvlekken: boolean
  /** Horizontale lensstrepen door felle lichtpunten (anamorfe lens). */
  lensStrepen: boolean
}

export interface EffectConfig {
  ao: boolean
  aoSterkte: number
  aoStraal: number
  bloom: boolean
  bloomSterkte: number
  bloomDrempel: number
  vignette: boolean
  vignetteSterkte: number
  /** Lineaire belichting vóór de tone mapping — het diafragma. */
  belichting: number
  /** Extra contrast in de AgX-look; 0 is de kale AgX-curve. */
  contrast: number
  /** Extra verzadiging in de AgX-look; 0 is de kale AgX-curve. */
  verzadiging: number
  /** Scherptediepte die meefocust met waar de camera naar kijkt. */
  dof: boolean
  dofBokeh: number
  /** Scherp bereik als fractie van de focusafstand. */
  dofBereik: number
  /** Filmkorrel, in weergave-eenheden. 0,03 is zichtbaar op 1 m, weg op 3 m. */
  korrel: number
  /** Radiale kleurschifting aan de beeldrand. */
  aberratie: number
  /** Split toning: hoe ver schaduwen en hooglichten naar hun tint schuiven. */
  gradatie: number
  schaduwTint: string
  lichtTint: string
  /**
   * Hoeveel kleur alles behalve het merkrood verliest: 0 is de kale look, 1
   * is zwart-wit met alleen het rood erin. De look van "From warehouse to
   * powerhouse" (Linde MH): een koele, bijna kleurloze hal waarin alleen het
   * rood van de trucks en de lichtlijnen spreekt.
   */
  merkrood: number
  smaa: boolean
}

export interface MagazijnConfig {
  gangpaden: number
  bays: number
  niveaus: number
  vulgraad: number
  seed: number
}

/** Aan/uit per objectgroep, plus aantallen waar dat zinvol is. */
export interface ObjectConfig {
  stellingen: boolean
  schoren: boolean
  aanrijdbeveiliging: boolean
  lading: boolean
  plafond: boolean
  sprinklers: boolean
  kabelgoten: boolean
  dockdeuren: boolean
  bollards: boolean
  vangrails: boolean
  laadstraat: boolean
  transportband: boolean
  palletstapels: boolean
  rolcontainers: boolean
  afvalbakken: boolean
  bebording: boolean
  wikkelaar: boolean
  gangpadborden: boolean
  nooduitgangen: boolean
  mezzanine: boolean
  spiegels: boolean
  displayborden: boolean
  stillages: boolean
  /**
   * De machines in de hal: de Matics, de C-MATIC-pendel, de schrobmachine en
   * de wikkelaar. Sinds 22 oktober uit: ze worden los verbeterd. Aan zet ze
   * terug, met hun eigen schakelaars hieronder.
   */
  machines: boolean
  /** Rode lichtlijnen onder de liggers aan de gangpadkant, als in de campagne van Linde MH. */
  lichtlijnen: boolean
  /**
   * De C-MATIC-palletpendel over het dwarspad. Heet nog `heftruck` omdat hier
   * eerst een bemande heftruck reed; zo blijven bewaarde Studio-standen geldig.
   */
  heftruck: boolean
  schrobmachine: boolean
  /**
   * De Matics als studiobeeld van Linde (`trucks/LindeBeeld.tsx`, 21 oktober).
   * Uit: de 3D-modellen, met heffen in de stelling en het overslagstation.
   */
  lindeBeeld: boolean
  /**
   * Het hoofdscherm als film van de zweefronde (`kiosk/zweeffilm.ts`, 22
   * oktober). Uit, of zonder film op de server: de hal realtime, zoals eerst.
   */
  zweeffilm: boolean
  stof: boolean
  vloermarkering: boolean
  trucks: number
}

/**
 * Oppervlak: textuurdetail, vloerreflectie, contactschaduw en variatie.
 *
 * Deze groep is nieuw omdat de scène tot nu toe uit vlakke kleuren bestond.
 * Alles staat hier als data en niet in de componenten, om dezelfde reden als de
 * rest van dit bestand: wat je op de beurs wilt kunnen bijstellen zonder een
 * build te draaien, hoort niet in code te staan.
 */
export interface OppervlakConfig {
  /** Procedurele PBR-maps op vloer, staal, karton, hout en wanden. */
  texturen: boolean
  /**
   * Resolutie van de grootste map (de vloer), afgerond naar een macht van twee.
   *
   * 512 met een tegel van 3 m geeft 5,9 mm per texel; 1024 met een tegel van
   * 4,5 m geeft 4,4 mm — vrijwel hetzelfde, voor een kwart van de laadtijd
   * (190 ms tegen 690 ms). Vandaar 512 als standaard.
   */
  textuurDetail: number
  /** Hoe diep de normal maps lijken. 0 is vlak, 1 is de ontworpen sterkte. */
  relief: number
  /** Spiegelende vloer. Kost een tweede scènerender — de duurste post hier. */
  vloerReflectie: boolean
  reflectieSterkte: number
  reflectieVervaging: number
  /** Resolutie van de reflectiebuffer. */
  reflectieResolutie: number
  /** Zachte schaduwranden (PCSS) in plaats van de harde shadow-map-rand. */
  zachteSchaduwen: boolean
  /** Per-instantie kleurafwijking op stellingen en lading. 0 is uniform. */
  variatie: number
}

export interface MateriaalConfig {
  staal: string
  ligger: string
  vloer: string
  pallet: string
  karton: string
  truckRood: string
  truckZwart: string
}

export interface AnimatieConfig {
  aan: boolean
  snelheid: number
  trucks: boolean
  mast: boolean
  transportband: boolean
  dockdeuren: boolean
  bakens: boolean
  stof: boolean
  cameraDrift: boolean
  ventilatoren: boolean
  laadindicatie: boolean
  wikkelaar: boolean
  /** Of de C-MATIC-palletpendel rijdt (zie `ObjectenConfig.heftruck`). */
  heftruck: boolean
  displays: boolean
  zwaailicht: boolean
}

export interface SceneConfig {
  licht: LichtConfig
  effecten: EffectConfig
  magazijn: MagazijnConfig
  objecten: ObjectConfig
  oppervlak: OppervlakConfig
  materialen: MateriaalConfig
  animatie: AnimatieConfig
}

export const STANDAARD_SCENE: SceneConfig = {
  licht: {
    omgeving: 0.7,
    ambient: 0.22,
    // Zon door de daklichten. Fel, want hij valt alleen nog in vlekken: het dak
    // werpt schaduw. Sinds 22 oktober minder fel en minder warm: in de look van
    // "From warehouse to powerhouse" is de hal een donkere, koele ruimte onder
    // harde armaturen, geen hal vol zonlicht.
    zonSterkte: 4.0,
    zonKleur: '#fbead2',
    armatuurSterkte: 1.0,
    armatuurKleur: '#e4ecfa',
    // Rustiger sinds 21 oktober ("AI-gehalte omlaag"): de rode merkgloed,
    // de lichtbundels, de lichtschachten en de lensstrepen maakten samen een
    // reclamebeeld. Ze blijven, maar als sfeer in plaats van als effect.
    // 22 oktober: de rode gloed weer iets op (15 → 35), voor de look van de
    // campagne van Linde MH — rood licht in een koele hal.
    accentSterkte: 35,
    accentKleur: '#E8213A',
    mistNabij: 30,
    mistVer: 120,
    mistKleur: '#0a0d13',
    schaduwen: true,
    vulSterkte: 0.9,
    daklichten: true,
    lichtschachten: true,
    schachtSterkte: 0.7,
    lichtband: true,
    daglicht: 1.0,
    bundels: true,
    // De harde lichtkegels onder de armaturen zijn het handschrift van de
    // campagnebeelden (22 oktober: 0,55 → 0,7).
    bundelSterkte: 0.7,
    lichtvlekken: true,
    lensStrepen: false,
  },
  // De filmlook rustiger (21 oktober). De teal-oranje kleuring, de
  // kleurschifting aan de rand, de zware bloom en de ondiepe scherptediepte
  // samen gaven het beeld van een stockrender — precies wat als "AI" werd
  // gelezen. De look blijft (AgX, korrel, vignet), elk effect een stap terug:
  // minder kleuring, minder gloed, een minder miniatuurachtige focus.
  //
  // 22 oktober: de kleuring van "From warehouse to powerhouse", de campagne
  // van Linde MH. Donkerder (belichting 1,35 → 1,2), meer contrast, koel
  // staalblauw in de schaduwen, koel wit in de hooglichten (geen warm oranje
  // meer) en alle kleur behalve het merkrood half weg (`merkrood`).
  effecten: {
    ao: true,
    aoSterkte: 1.8,
    aoStraal: 1.1,
    bloom: true,
    bloomSterkte: 0.32,
    bloomDrempel: 1.15,
    vignette: true,
    vignetteSterkte: 0.45,
    belichting: 1.2,
    contrast: 0.22,
    verzadiging: 0.04,
    dof: true,
    dofBokeh: 1.2,
    dofBereik: 2.0,
    korrel: 0.018,
    aberratie: 0.0015,
    gradatie: 0.32,
    schaduwTint: '#4c6a82',
    lichtTint: '#c7d1dc',
    merkrood: 0.55,
    smaa: true,
  },
  magazijn: {
    gangpaden: 5,
    bays: 19,
    niveaus: 4,
    vulgraad: 0.74,
    seed: 20261103,
  },
  objecten: {
    stellingen: true,
    schoren: true,
    aanrijdbeveiliging: true,
    lading: true,
    plafond: true,
    sprinklers: true,
    kabelgoten: true,
    dockdeuren: true,
    bollards: true,
    vangrails: true,
    laadstraat: true,
    transportband: true,
    palletstapels: true,
    rolcontainers: true,
    afvalbakken: true,
    bebording: true,
    wikkelaar: true,
    gangpadborden: true,
    nooduitgangen: true,
    mezzanine: true,
    spiegels: true,
    displayborden: true,
    stillages: true,
    machines: false,
    lichtlijnen: true,
    heftruck: true,
    schrobmachine: true,
    lindeBeeld: true,
    zweeffilm: true,
    stof: true,
    vloermarkering: true,
    trucks: 4,
  },
  oppervlak: {
    texturen: true,
    textuurDetail: 512,
    relief: 1,
    vloerReflectie: true,
    reflectieSterkte: 0.34,
    reflectieVervaging: 0.65,
    reflectieResolutie: 512,
    zachteSchaduwen: true,
    variatie: 0.12,
  },
  materialen: {
    staal: '#2b3039',
    ligger: '#7a2630',
    // Gepolijst beton is middengrijs, geen zwart: het is het grootste vlak dat
    // licht terugkaatst, en een zwarte vloer maakt van de hal een zwart gat.
    vloer: '#34373c',
    pallet: '#6b5334',
    karton: '#a89070',
    // Bemonsterd uit de vlakke, belichte vlakken van de fabrieksfoto's:
    // kunststof [78,78,78]. De "zwarte" delen van een Linde zijn antraciet —
    // pikzwart maakt er een silhouet van in plaats van een machine.
    //
    // Het rood komt van de draaitafels op linde-mh.com (hun eigen renders,
    // zonder de kleurcorrectie van een foto): de belichte lak is [212,19,19]
    // op de R- en K-MATIC, [174,21,25] op de L-MATIC — groen en blauw vrijwel
    // gelijk en heel laag. Lineair is dat vijf keer verzadigder dan het
    // [193,34,43] van hiervoor, dat naar karmijn neigde en onder de filmlook
    // zalmroze werd.
    truckRood: '#c91711',
    truckZwart: '#3c3d42',
  },
  animatie: {
    aan: true,
    snelheid: 1,
    trucks: true,
    mast: true,
    transportband: true,
    dockdeuren: true,
    bakens: true,
    stof: true,
    cameraDrift: true,
    ventilatoren: true,
    laadindicatie: true,
    wikkelaar: true,
    heftruck: true,
    displays: true,
    zwaailicht: true,
  },
}

/* ---------- Prestatieprofielen ---------- */

/**
 * Twee profielen voor wat de kiosk per beeld tekent (6 oktober, PLAN.md §0.10).
 * `vol` is de scène zoals ontworpen; `licht` is voor een pc zonder stevige
 * GPU. Het lichte profiel laat de hal herkenbaar dezelfde, maar zet de vijf
 * duurste posten uit of lager:
 *
 *  - pixeldichtheid 1 in plaats van tot 2 — op een 4K-paneel een kwart van de
 *    pixels voor élke pass hieronder;
 *  - geen vloerreflectie (die tekent de hele hal een tweede keer);
 *  - harde schaduwen op een kaart van 1024 in plaats van PCSS op 2048;
 *  - geen ambient occlusion, scherptediepte of lichtschachten (drie
 *    beeldvullende passes, de schachten met raymarching);
 *  - een kwart van het stof en texturen op 256 in plaats van 512, wat ook het
 *    opstarten sneller maakt.
 *
 * Bloom, filmlook en SMAA blijven: die zijn goedkoop en dragen de look.
 */
export type Profiel = 'vol' | 'licht'

/** Wat een profiel buiten het scènedocument om bepaalt. */
export const PROFIEL_DETAIL: Record<Profiel, { dpr: number | [number, number]; schaduwkaart: number; stof: number }> = {
  // Stof 6.000 → 3.500 (21 oktober): een wolk onscherpe schijfjes vlak voor
  // de lens las als effect, niet als lucht in een hal.
  vol: { dpr: [1, 2], schaduwkaart: 2048, stof: 3500 },
  licht: { dpr: 1, schaduwkaart: 1024, stof: 1500 },
}

/** Het scènedocument met de instellingen van een profiel erover. */
export function metProfiel(scene: SceneConfig, profiel: Profiel): SceneConfig {
  const vol = profiel === 'vol'
  const s = STANDAARD_SCENE
  return {
    ...scene,
    licht: { ...scene.licht, lichtschachten: vol ? s.licht.lichtschachten : false },
    effecten: {
      ...scene.effecten,
      ao: vol ? s.effecten.ao : false,
      dof: vol ? s.effecten.dof : false,
    },
    oppervlak: {
      ...scene.oppervlak,
      vloerReflectie: vol ? s.oppervlak.vloerReflectie : false,
      zachteSchaduwen: vol ? s.oppervlak.zachteSchaduwen : false,
      textuurDetail: vol ? s.oppervlak.textuurDetail : 256,
    },
  }
}
